import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { attachmentService, Attachment, AttachmentEntityType } from '../../services/httpServices/attachmentService';

export interface AttachmentsState {
  items: Attachment[];
  loading: boolean;
  uploading: boolean;
  deletingId: string | null;
  downloadingId: string | null;
  error: string | null;
  selectedEntityType: AttachmentEntityType | null;
  selectedEntityId: string | null;
}

const initialState: AttachmentsState = {
  items: [],
  loading: false,
  uploading: false,
  deletingId: null,
  downloadingId: null,
  error: null,
  selectedEntityType: null,
  selectedEntityId: null,
};

export const fetchAttachments = createAsyncThunk(
  'attachments/fetch',
  async (
    { entityType, entityId }: { entityType: AttachmentEntityType; entityId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await attachmentService.getAttachments(entityType, entityId);
      return { items: response.data || [], entityType, entityId };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to load attachments');
    }
  },
);

export const uploadAttachment = createAsyncThunk(
  'attachments/upload',
  async (
    {
      file,
      entityType,
      entityId,
      description,
    }: {
      file: File;
      entityType: AttachmentEntityType;
      entityId: string;
      description?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await attachmentService.uploadFile(file, entityType, entityId, description);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Upload failed');
    }
  },
);

export const deleteAttachment = createAsyncThunk(
  'attachments/delete',
  async (
    { id, entityType, entityId }: { id: string; entityType: AttachmentEntityType; entityId: string },
    { rejectWithValue },
  ) => {
    try {
      await attachmentService.deleteAttachment(id);
      return { id, entityType, entityId };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Delete failed');
    }
  },
);

export const getDownloadUrl = createAsyncThunk(
  'attachments/downloadUrl',
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const response = await attachmentService.getDownloadUrl(id);
      return { id, downloadUrl: response.data?.downloadUrl || '' };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to get download URL');
    }
  },
);

const attachmentsSlice = createSlice({
  name: 'attachments',
  initialState,
  reducers: {
    clearAttachmentError: (state) => {
      state.error = null;
    },
    clearAttachments: (state) => {
      state.items = [];
      state.selectedEntityType = null;
      state.selectedEntityId = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchAttachments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAttachments.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.selectedEntityType = action.payload.entityType;
        state.selectedEntityId = action.payload.entityId;
      })
      .addCase(fetchAttachments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Upload
      .addCase(uploadAttachment.pending, (state) => {
        state.uploading = true;
        state.error = null;
      })
      .addCase(uploadAttachment.fulfilled, (state, action) => {
        state.uploading = false;
        if (action.payload) {
          state.items.unshift(action.payload);
        }
      })
      .addCase(uploadAttachment.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.payload as string;
      })
      // Delete
      .addCase(deleteAttachment.pending, (state, action) => {
        state.deletingId = action.meta.arg.id;
        state.error = null;
      })
      .addCase(deleteAttachment.fulfilled, (state, action) => {
        state.deletingId = null;
        state.items = state.items.filter((item) => item.id !== action.payload.id);
      })
      .addCase(deleteAttachment.rejected, (state, action) => {
        state.deletingId = null;
        state.error = action.payload as string;
      })
      // Download URL
      .addCase(getDownloadUrl.pending, (state, action) => {
        state.downloadingId = action.meta.arg.id;
      })
      // Reducers must stay pure — opening a tab here would run on every state
      // replay (e.g. Redux DevTools time-travel). The caller opens the URL.
      .addCase(getDownloadUrl.fulfilled, (state) => {
        state.downloadingId = null;
      })
      .addCase(getDownloadUrl.rejected, (state) => {
        state.downloadingId = null;
      });
  },
});

export const { clearAttachmentError, clearAttachments } = attachmentsSlice.actions;
export default attachmentsSlice.reducer;
